import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Article,
  Publish,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../services/auth";
import { blogService } from "../services/api";
import { toast } from "react-toastify";

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalPosts: 0,
    publishedPosts: 0,
    draftPosts: 0,
    wordpressPosts: 0,
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const postsResponse = await blogService.getUserPosts();
      const posts = postsResponse.data.data.data || [];

      const wordpressPosts = posts.filter((post) => post.wordpress_id).length;

      setStats({
        totalPosts: posts.length,
        publishedPosts: posts.filter(p => p.wordpress_id != null && p.wordpress_id !== "").length,
        // draftPosts: posts.filter((p) => p.wordpress_id == "NULL").length,
        wordpressPosts,
      });

      setRecentPosts(posts.slice(0, 5));
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      await blogService.deletePost(postId);
      toast.success("Post deleted successfully");
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Welcome back, {user?.name}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's what's happening with your blog today.
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" variant="overline">
                    Total Posts
                  </Typography>
                  <Typography variant="h4">{stats.totalPosts}</Typography>
                </Box>
                <Article sx={{ fontSize: 40, color: "primary.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" variant="overline">
                    Published
                  </Typography>
                  <Typography variant="h4">{stats.publishedPosts}</Typography>
                </Box>
                <Publish sx={{ fontSize: 40, color: "success.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" variant="overline">
                    Drafts
                  </Typography>
                  <Typography variant="h4">{stats.draftPosts}</Typography>
                </Box>
                <Schedule sx={{ fontSize: 40, color: "warning.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>

      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<Add />}
              component={RouterLink}
              to="/create-post"
            >
              New Post
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              component={RouterLink}
              to="/"
            >
              View Website
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6">Recent Posts</Typography>
          <Button component={RouterLink} to="/my-posts">
            View All
          </Button>
        </Box>

        {recentPosts.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              You haven't created any posts yet.
            </Typography>
            <Button
              variant="contained"
              component={RouterLink}
              to="/create-post"
              startIcon={<Add />}
            >
              Create Your First Post
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  {/* <TableCell>Status</TableCell> */}
                  <TableCell>WordPress</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentPosts.map((post) => (
                  <TableRow key={post.id}>
                    <TableCell>
                      <Typography
                        component={RouterLink}
                        to={`/post/${post.id}`}
                        sx={{
                          textDecoration: "none",
                          color: "text.primary",
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        {post.title}
                      </Typography>
                    </TableCell>
                    {/* <TableCell>
                      <Chip
                        label={post.status || "draft"}
                        size="small"
                        color={
                          post.status === "published" ? "success" : "default"
                        }
                        variant="outlined"
                      />
                    </TableCell> */}

                    <TableCell>
                      {post.wordpress_id ? (
                        <Chip label="Published" size="small" color="success" />
                      ) : (
                        <Chip
                          label="Not Sent"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(post.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={`/post/${post.id}`}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={`/edit-post/${post.id}`}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeletePost(post.id)}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default Dashboard;
